<template>
  <li
    class="pix-navbar-menu-item__item"
    :class="{
      'pix-navbar-menu-item__item--has-separator': menuItem.hasSeparator,
      'pix-navbar-menu-item__item--parent': isFirstItem,
      'pix-navbar-menu-item__item--open': isFirstItem && isOpen,
    }"
    @click.left="handleClick(menuItem)"
    @click.middle="handleClick(menuItem)"
    v-click-outside="closeMenu"
    ref="pix-navbar-menu-item"
  >
    <!-- Renders an arrow indicating the direction of the nested items. Because this will appear on the left side it has to be the first item -->
    <span v-if="!isFirstItem && hasChildren && expandDirection === ExpandDirections.Left" class="pix-navbar-menu-item__icon mdi mdi-menu-right" />

    <!-- Renders an image at the left side of the label -->
    <span v-if="menuItem.imgSource" class="pix-navbar-menu-item__image">
      <pix-flag :country="menuItem.imgSource" />
    </span>

    <!-- Renders either a router-link or a label depending on the value of the route property of the menuItem -->
    <router-link v-if="menuItem.route" :to="menuItem.route" class="pix-navbar-menu-item__label">
      {{ translation }}
    </router-link>
    <span v-else class="pix-navbar-menu-item__label" :class="{ 'pix-navbar-menu-item__label--bold': boldLabel }">
      {{ translation }}
    </span>

    <!-- Renders an arrow indicating the direction of the nested items -->
    <span v-if="isFirstItem && hasChildren" class="pix-navbar-menu-item__icon mdi mdi-menu-down" />
    <span v-if="!isFirstItem && hasChildren && expandDirection === ExpandDirections.Right" class="pix-navbar-menu-item__icon mdi mdi-menu-left" />

    <!-- There are a couple of special "items" that display their own template  -->
    <template v-if="displayLogout">
      <span class="mdi mdi-account" />
      <span>{{ userName }}</span>
    </template>

    <!-- Renders another list of items if the menuItem has childrens. Inside this list it will call this component recursively -->
    <ul
      v-if="hasChildren && ((isFirstItem && isOpen) || !isFirstItem)"
      class="pix-navbar-menu-item__submenu"
      :class="{
        'pix-navbar-menu-item__submenu--first-submenu': isFirstItem,
        'pix-navbar-menu-item__submenu--left': expandDirection === ExpandDirections.Left,
        'pix-navbar-menu-item__submenu--right': expandDirection === ExpandDirections.Right,
      }"
    >
      <pix-navbar-menu-item v-for="children in menuItem.children" :key="children.label" :menu-item="children" :expand-direction="expandDirection" />
    </ul>

    <!-- Renders a separation line depending on the hasSeparator property of the menuItem -->
    <div v-if="menuItem.hasSeparator" class="pix-navbar-menu-item__separator" />
  </li>
</template>

<script>
import ExpandDirections from './expandDirections.js';
import pixFlag from '@/components/pix/pixFlag.vue';
import { mapState, mapGetters } from 'vuex';
import clickOutsideDirective from '@/directives/clickOutside.directive.js';
import jllGoogleAnalytics from 'jll-google-analytics';
import webAnalyticsUtils from '@/utils/webAnalytics.utils';
import webAnalyticsEventsConst from '@/consts/webAnalyticsEvents.const';

export default {
  name: 'pix-navbar-menu-item',

  directives: {
    'click-outside': clickOutsideDirective,
  },

  components: {
    pixFlag,
  },

  props: {
    menuItem: { type: Object, default: () => {} },
    isFirstItem: { type: Boolean, default: false },
    expandDirection: { type: String, default: ExpandDirections.Right },
  },

  data() {
    return {
      ExpandDirections,
      isOpen: false,
    };
  },

  computed: {
    hasChildren() {
      return this.menuItem.children?.length;
    },

    translation() {
      return this.$t(this.menuItem.label);
    },

    displayLogout() {
      return this.menuItem.name && this.menuItem.name === 'logout';
    },

    displayTime() {
      return this.menuItem.name && this.menuItem.name === 'time';
    },

    boldLabel() {
      // The label will show a bold style under certain conditions
      if (this.menuItem.name?.includes('instance-')) {
        const itemInstance = this.menuItem.name.split('-')[1];
        return itemInstance === this.instance;
      }

      if (this.menuItem.name?.includes('lang-')) {
        const itemLang = this.menuItem.name.split('-')[1];
        return itemLang === this.$i18n.locale;
      }

      return false;
    },

    ...mapGetters({
      instance: 'authModule/instance',
      userName: 'authModule/UserFullName',
      isAuthenticated: 'authModule/isAuthenticated',
    }),

    ...mapState({
      geaUrl: (state) => state.geaUrl,
    }),
  },

  methods: {
    closeMenu() {
      this.isOpen = false;
    },

    toggleIsOpen() {
      this.isOpen = !this.isOpen;
    },

    handleClick({ fn }) {
      fn && this.handleFunction(fn);
      // Moving the execution to the end of the callstack will avoid that the menu item gets destroyed before executing an action (for instance, the router link navegation)
      setTimeout(() => this.toggleIsOpen());
    },

    handleFunction(fn) {
      fn(this);
    },

    async changeInstance(newInstance) {
      if (newInstance !== this.instance) {
        webAnalyticsUtils.registerWebAnalyticsEvent(webAnalyticsEventsConst.CHANGE_INSTANCE);
        await this.$store.dispatch('changeInstance', newInstance);
      }
    },

    changeLanguage(newLanguage) {
      this.$i18n.locale = newLanguage;
      this.$store.dispatch('setLangCode', newLanguage);
      this.$store.dispatch('setShowApp', false);
      location.reload();
    },

    redirectToGea(url) {
      const nextUrl = `${this.geaUrl}/${url}`;
      window.open(nextUrl, '_blank');
    },

    replaceUrl(destinationHost) {
      const urlString = window.location.href;
      const url = new URL(urlString);
      const host = url.host;
      const newUrl = urlString.replace(host, destinationHost);
      window.open(newUrl);
    },

    openTREPP() {
      const treppURL = 'https://apps.joneslanglasalle.com/Home/RSReportEngine/showreport.aspx?report=TREPP_Property_Report.rdl&appkey=291&ParamKey=254&__showReport=true';
      window.open(treppURL, '_blank');
      jllGoogleAnalytics.registerCustomEvent('navigation from PIX to TREPP');
    },
  },
};
</script>

<style scoped>
.pix-navbar-menu-item__item {
  position: relative;
  cursor: pointer;

  height: 100%;

  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;

  transition: background-color 0.1s ease-in;
}

.pix-navbar-menu-item__item--parent {
  padding: 0 10px;
}

.pix-navbar-menu-item__item--has-separator {
  margin-bottom: 9px;
}

.pix-navbar-menu-item__item--open {
  background: rgba(0, 0, 0, 0.3);
}

.pix-navbar-menu-item__item--open .pix-navbar-menu-item__icon {
  transform: rotate(180deg);
}

.pix-navbar-menu-item__icon {
  /* transition: transform 0.2s ease-in-out; */
}

.pix-navbar-menu-item__image {
  width: 16px;
}

.pix-navbar-menu-item__label {
  flex: 1;
  text-decoration: none;
  color: inherit;

  letter-spacing: 0.3px;
}

.pix-navbar-menu-item__label--bold {
  font-weight: 700;
}

.pix-navbar-menu-item__separator {
  height: 8px;
  width: 100%;
  border-bottom: solid 1px #e9ecef;
  position: absolute;
  left: 0;
  bottom: -4px;
}

.pix-navbar-menu-item__logout-btn {
  background: none;
  border: none;
}

.pix-navbar-menu-item__item--open > .pix-navbar-menu-item__submenu {
  display: block;
}

.pix-navbar-menu-item__submenu {
  position: absolute;
  top: 0;
  display: none;

  margin: 0;
  padding: 0.5rem 0;

  list-style: none;
  white-space: nowrap;

  background: #fff;
  color: #212529;
  border: solid 1px #ccc;
  border-radius: 3px;

  font-size: 14px;
  font-weight: 400;
  letter-spacing: 0px;

  z-index: 999;
}

.pix-navbar-menu-item__submenu .pix-navbar-menu-item__submenu {
  min-width: 100px;
}

.pix-navbar-menu-item__submenu .pix-navbar-menu-item__item {
  height: 30px;
  gap: 1rem;
  padding: 3px 20px;
  min-width: 110px;
}

.pix-navbar-menu-item__submenu .pix-navbar-menu-item__item:hover {
  background-color: #f5f5f5;
}

/* The first submenu instance will be shown under it's menu-item */
.pix-navbar-menu-item__submenu--first-submenu {
  top: 100%;
}

/* Hovering a menu-item will display its submenus */
.pix-navbar-menu-item__submenu .pix-navbar-menu-item__item:hover > .pix-navbar-menu-item__submenu {
  display: block;
}

/* Nested submenus will positionate on the expand-direction selected */
.pix-navbar-menu-item__submenu--right {
  left: 0;
}
.pix-navbar-menu-item__submenu--left {
  right: 0;
}
.pix-navbar-menu-item__submenu--left .pix-navbar-menu-item__item {
  justify-content: flex-end;
}
.pix-navbar-menu-item__submenu--left .pix-navbar-menu-item__label {
  text-align: right;
}
.pix-navbar-menu-item__submenu--right .pix-navbar-menu-item__submenu {
  left: 100%;
}
.pix-navbar-menu-item__submenu--left .pix-navbar-menu-item__submenu {
  right: 100%;
}
</style>
